const UUID_BYTE_LENGTH = 16;
const TIMESTAMP_MASK = 0xff;
const COUNTER_MASK = 0x0fff;

let lastTimestamp = 0;
let lastCounter = 0;

function getRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
    return bytes;
  }
  for (let index = 0; index < length; index += 1) {
    bytes[index] = Math.floor(Math.random() * 256);
  }
  return bytes;
}

function waitForNextMillisecond(previousTimestamp: number): number {
  let currentTimestamp = Date.now();
  while (currentTimestamp <= previousTimestamp) {
    currentTimestamp = Date.now();
  }
  return currentTimestamp;
}

function formatUuid(bytes: Uint8Array): string {
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createUuidV7(): string {
  let timestamp = Date.now();
  if (timestamp <= lastTimestamp) {
    lastCounter = (lastCounter + 1) & COUNTER_MASK;
    if (lastCounter === 0) {
      timestamp = waitForNextMillisecond(lastTimestamp);
    } else {
      timestamp = lastTimestamp;
    }
  } else {
    lastCounter = 0;
  }

  lastTimestamp = timestamp;

  const bytes = new Uint8Array(UUID_BYTE_LENGTH);
  const randomBytes = getRandomBytes(8);

  bytes[0] = Math.floor(timestamp / 2 ** 40) & TIMESTAMP_MASK;
  bytes[1] = Math.floor(timestamp / 2 ** 32) & TIMESTAMP_MASK;
  bytes[2] = Math.floor(timestamp / 2 ** 24) & TIMESTAMP_MASK;
  bytes[3] = Math.floor(timestamp / 2 ** 16) & TIMESTAMP_MASK;
  bytes[4] = Math.floor(timestamp / 2 ** 8) & TIMESTAMP_MASK;
  bytes[5] = timestamp & TIMESTAMP_MASK;
  bytes[6] = 0x70 | ((lastCounter >> 8) & 0x0f);
  bytes[7] = lastCounter & TIMESTAMP_MASK;
  bytes[8] = 0x80 | (randomBytes[0] & 0x3f);
  bytes.set(randomBytes.slice(1), 9);

  return formatUuid(bytes);
}

function createPrefixedEntityId(prefix: string): string {
  return `${prefix}_${createUuidV7()}`;
}

export function createGraphNodeEntityId(): string {
  return createPrefixedEntityId('nd');
}

export function createSceneNodeEntityId(): string {
  return createPrefixedEntityId('sn');
}

export function createComponentEntityId(): string {
  return createPrefixedEntityId('cmp');
}

export function createDocumentEntityId(): string {
  return createPrefixedEntityId('doc');
}

export function createFavoriteEntityId(): string {
  return createPrefixedEntityId('fav');
}

export function createUserLibraryEntityId(): string {
  return createPrefixedEntityId('user');
}

export function createLayerEntityId(): string {
  return createPrefixedEntityId('layer');
}

/**
 * A data mutation's id, as the engine mints it (`fluveria_document::ids::mutation`):
 * a server hears it from every client and a queue may keep it across a
 * restart, so it is unique beyond this runtime (kb:decision/14).
 */
export function createMutationEntityId(): string {
  return createPrefixedEntityId('mut');
}

export function createBuiltInGeometryId(key: string): string {
  return `geo_${key}`;
}

export function createBuiltInMaterialId(key: string): string {
  return `mat_${key}`;
}

export function getDefaultGeometryLibraryId(): string {
  return createBuiltInGeometryId('default');
}

export function getDefaultMaterialLibraryId(): string {
  // mat_themed: new layers show their geometry (it has a Mask, unlike the
  // flat mat_flat) and respect the light/dark render theme out of the box.
  return createBuiltInMaterialId('themed');
}
